<template>
  <div>
    <ElForm
      :model="ruleForm"
      :rules="rules"
      :label-position="labelPosition"
      ref="auth-form"
      label-width="120px"
      status-icon
      @submit.native.prevent
      v-loading="authLoading"
    >
      <ElFormItem
        v-if="isSignUp"
        prop="name"
        label="First & Last name"
      >
        <ElInput
          v-model="ruleForm.name"
          name="username"
          placeholder="Full Name"
        />
      </ElFormItem>

      <ElFormItem
        v-if="!isResetPasswordSetPassword"
        prop="email"
        label="Email"
      >
        <ElInput
          ref="input-email"
          v-model.trim="ruleForm.email"
          name="email"
          auto-complete="on"
          placeholder="name@example.com"
          inputmode="email"
          @keyup.enter.native="isResetPasswordSendEmail ? { keyup: onFormSubmit(true) } : {}"
        />
      </ElFormItem>

      <ElFormItem
        v-if="isSignIn || isSignUp || isResetPasswordSetPassword"
        prop="password"
      >
        <div
          slot="label"
          class="flex justify-between"
        >
          <span>
            Password
          </span>
          <RouterLink
            v-if="isSignIn"
            ref="reset-password"
            :tabindex="forgotPasswordTabIndex"
            :to="{ name: 'AuthResetPasswordSendEmail' }"
            class="font-normal text-xs text-base text-primary-light hover:underline"
            @keydown.tab.native="onResetPasswordTabPress($event)"
            @blur.native="forgotPasswordTabIndex = -1"
          >Forgot password?</RouterLink>
        </div>
        <ElInput
          ref="input-password"
          v-model="ruleForm.password"
          name="password"
          :auto-complete="isSignIn ? 'current-password' : 'new-password'"
          type="password"
          placeholder="*******"
          @keydown.tab.native="onPasswordTabPress($event)"
        />
      </ElFormItem>

      <ElFormItem class="submit-container">
        <ElButton
          ref="main-button"
          type="primary"
          native-type="submit"
          class="submit-button touch:w-full"
          @click="onFormSubmit(true)"
        >
          {{ getFormOptions.mainButtonLabel }}
        </ElButton>
        <RouterLink
          :to="{ name: getFormOptions.mainRouteName }"
          class="text-primary ml-20 hover:underline touch:block touch:text-center touch:ml-0 touch:mt-7"
        >{{ getFormOptions.mainRouteLabel }}</RouterLink>
      </ElFormItem>
    </ElForm>

    <template v-if="isSignIn">
      <p class="font-bold text-primary text-center py-40 touch:py-20 mobile:py-15">
        - OR -
      </p>

      <div class="flex items-end justify-between -mx-10 touch:flex-col">
        <div class="w-full px-10">
          <ElButton
            type="primary"
            class="w-full bg-brand-google border-brand-google focus:bg-brand-google focus:border-brand-google
          focus:opacity-75 hover:bg-brand-google hover:border-brand-google hover:opacity-75"
            @click="onFormSubmit(false, 'signUserInWithGoogle')"
          >
            <FontAwesomeIcon
              :icon="['fab', 'google']"
              size="lg"
              class="mr-15"
            />
            Sign in with Google
          </ElButton>
        </div>
        <div class="w-full px-10">
          <ElButton
            type="primary"
            class="w-full px-10 bg-brand-facebook border-brand-facebook focus:bg-brand-facebook
          focus:border-brand-facebook focus:opacity-75 hover:bg-brand-facebook hover:border-brand-facebook
          hover:opacity-75 touch:mt-10"
            @click="onFormSubmit(false, 'signUserInWithFacebook')"
          >
            <FontAwesomeIcon
              :icon="['fab', 'facebook-f']"
              size="lg"
              class="mr-15"
            />
            Sign in with Facebook
          </ElButton>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
/* eslint-disable max-len */
import { mapState } from 'vuex';
import { showErrorNotification, showSuccessNotification } from '@/utils/notification';

export default {
  props: {
    isSignIn: Boolean,
    isSignUp: Boolean,
    isResetPasswordSendEmail: Boolean,
    isResetPasswordSetPassword: Boolean,
  },
  data() {
    const validateName = (rule, value, callback) => {
      if (value === '') {
        callback('Please enter a name.');
      } else if (value.length >= 256) {
        callback('Your name is too long.');
      }
      callback();
    };
    const validateEmail = (rule, value, callback) => {
      const re = /^.+@.+\..+$/;

      if (value.length === 0) {
        callback('Please enter your Email.');
      } else if (!re.test(value) || value.length >= 256 || value.includes(' ')) {
        callback('Please enter a valid Email.');
      }
      callback();
    };
    const validatePassword = (rule, value, callback) => {
      if (value === '') {
        callback('Please enter your password.');
      } else if (value.length < 6) {
        callback('Please enter a valid password (more than 5 characters)');
      }
      callback();
    };

    return {
      labelPosition: 'top',
      authLoading: false,
      forgotPasswordTabIndex: -1,
      passwordTabIndex: 0,
      ruleForm: {
        name: '',
        email: '',
        password: '',
      },
      rules: {
        name: [{
          validator: validateName,
          trigger: 'blur',
        }],
        email: [{
          validator: validateEmail,
          trigger: 'blur',
        }],
        password: [{
          validator: validatePassword,
          trigger: 'change',
        }],
      },
    };
  },
  computed: {
    ...mapState('user/auth', {
      userAuthData: 'authData',
    }),
    getFormOptions() {
      if (this.isSignIn) {
        return {
          mainButtonLabel: 'Sign in',
          mainRouteName: 'AuthSignUp',
          mainRouteLabel: 'Create new account',
        };
      }
      if (this.isSignUp) {
        return {
          mainButtonLabel: 'Create',
          mainRouteName: 'AuthSignIn',
          mainRouteLabel: 'Already have an account?',
        };
      }
      if (this.isResetPasswordSendEmail) {
        return {
          mainButtonLabel: 'Reset',
          mainRouteName: 'AuthSignIn',
          mainRouteLabel: 'Back to sign in',
        };
      }
      if (this.isResetPasswordSetPassword) {
        return {
          mainButtonLabel: 'Save',
          mainRouteName: 'AuthSignIn',
          mainRouteLabel: 'Back to sign in',
        };
      }
      return {
        mainButtonLabel: '',
        mainRouteName: '',
        mainRouteLabel: '',
      };
    },
  },
  methods: {
    onPasswordTabPress(e) {
      if (!this.isSignIn) return;

      e.preventDefault();

      if (!e.shiftKey) { // Tab only
        this.forgotPasswordTabIndex = 0;
        this.$refs['reset-password'].$el.focus();
        return;
      }

      // Tab + Shift
      this.$refs['input-email'].focus();
    },
    onResetPasswordTabPress(e) {
      if (!this.isSignIn) return;

      e.preventDefault();

      if (!e.shiftKey) { // Tab only
        this.forgotPasswordTabIndex = -1;
        this.passwordTabIndex = -1;
        this.$refs['main-button'].$el.focus();
        return;
      }

      this.forgotPasswordTabIndex = -1;
      this.$refs['input-password'].focus();
    },
    async onFormSubmit(validateFields, submitType) {
      if (this.$refs['auth-form'] && validateFields) {
        const formIsValidated = await this.$refs['auth-form'].validate().then((valid) => valid).catch((valid) => valid);
        if (!formIsValidated) return;
      }

      try {
        if (this.isSignIn) {
          if (submitType === 'signUserInWithGoogle') {
            await this.$store.dispatch('user/auth/signUserInWithGoogle');
          } else if (submitType === 'signUserInWithFacebook') {
            await this.$store.dispatch('user/auth/signUserInWithFacebook');
          } else {
            this.authLoading = true;
            await this.$store.dispatch('user/auth/signUserInWithEmailPassword', {
              email: this.ruleForm.email,
              password: this.ruleForm.password,
            });
          }

          this.$router.push({ name: 'Dashboard' });
          showSuccessNotification('You signed in successfully.', 1000);
        } else if (this.isSignUp) {
          this.authLoading = true;
          await this.$store.dispatch('user/auth/signUserUpWithEmailPassword', {
            email: this.ruleForm.email,
            password: this.ruleForm.password,
            username: this.ruleForm.name,
          });
          this.$router.push({ name: 'Dashboard' });
          showSuccessNotification('You signed up successfully. We sent you a link to verify'
            + ' your Email. If you do not find it in Inbox, please also check your Junk/Spam'
            + ' folder.', 10000, true);
        } else if (this.isResetPasswordSendEmail) {
          this.authLoading = true;
          await this.$store.dispatch('user/auth/sendPasswordResetEmail', {
            email: this.ruleForm.email,
          });
          this.$router.push({ name: 'AuthSignIn' });
          showSuccessNotification('We sent you a link to reset your password by Email. If you do not find it in Inbox, please check'
            + ' your Junk/Spam folder', 10000, true);
        } else if (this.isResetPasswordSetPassword) {
          this.authLoading = true;
          await this.$store.dispatch('user/auth/handleResetPasswordAction', {
            actionCode: this.$route.query.actionCode,
            email: this.userAuthData.email,
            password: this.ruleForm.password,
          });

          await this.$store.dispatch('user/auth/signUserInWithEmailPassword', {
            email: this.$route.query.email,
            password: this.ruleForm.password,
          });
          this.$router.push({ name: 'Dashboard' });
          showSuccessNotification('You reset your password successfully.', 1000);
        }
      } catch (error) {
        this.authLoading = false;
        showErrorNotification(error.message);
      }
    },
  },
};
</script>

<style scoped>
>>> .el-form-item label {
  @apply w-full;
}

.submit-container {
  @apply mb-0;

  .submit-button {
    @apply px-70;
  }
}
</style>
